import { Scene } from 'phaser';
import { BotaoArcade } from '../components/BotaoArcade';
import { desenharPaginaLima } from '../components/cenario';
import { ModalArcade } from '../components/ModalArcade';
import { criarTextoRico } from '../components/TextoRico';
import { SCENES, TEX } from '../config/constants';
import { ARCADE, ARCADE_HEX, ARCADE_TEXT } from '../config/theme';
import { Sessao } from '../services/Sessao';
import { montarPlacarVersus, ProgressoVersus } from '../services/versus';
import { t } from '../services/textos';

const PARAGRAFOS = ['versus.res_texto_1', 'versus.res_texto_2', 'versus.res_texto_3'];

/**
 * Modo Versus — espera pela avaliação, pontuação e finalização (Figma,
 * quadros 79, 80 e 28). A nota é lançada pelo facilitador (RN-MV-16) e
 * chega por evento: o tablet não calcula nada. RN-MV-21: é pontuação de
 * grupo, fora do ranking individual.
 */
export class VersusResultadoScene extends Scene {
    constructor () {
        super(SCENES.VERSUS_RESULTADO);
    }

    async create () {
        // `scene.start` repetido reinicia esta mesma instância e o `create` antigo, parado num `await`,
        // retomaria depois do reinício (a cena volta a estar ativa) e desenharia uma 2ª cópia por cima.
        const geracao = this.geracao = (this.geracao ?? 0) + 1;
        if (!Sessao.ativa) {
            this.scene.start(SCENES.LOGIN);
            return;
        }
        this.modal = null;
        this.tela = [];
        const fonte = this.registry.get('fonte');
        this.progresso = new ProgressoVersus(Sessao.atual.participanteId);

        desenharPaginaLima(this, { titulo: t('versus.titulo'), rotuloJogador: Sessao.rotulo, rotuloY: 128, espacamento: 0 });
        this.add.image(64, 48, TEX.BTN_INSIGHTS_REDONDO).setOrigin(0)
            .setInteractive({ useHandCursor: true })
            .on('pointerup', () => !this.modal && this.scene.start(SCENES.AGUARDE, {}));

        try {
            this.versus = await fonte.obterCatalogoVersus();
        } catch (falha) {
            console.error('[Versus] catálogo indisponível para montar a pontuação.', falha);
            this.versus = { rodadas: [], criterios: [] };
        }
        if (!this.scene.isActive() || geracao !== this.geracao) return;

        if (this.progresso.estado.avaliacao) {
            this.mostrarPontuacao();
            return;
        }

        // RI-MV-14: enquanto a avaliação não é liberada, o participante espera.
        this.tela = [
            this.add.text(640, 372, t('versus.res_enviados'), { ...ARCADE_TEXT.manchete, fontSize: '34px' }).setOrigin(0.5),
            this.add.text(640, 440, t('versus.res_aguarde'), {
                ...ARCADE_TEXT.aviso, fontSize: '26px', lineSpacing: 6
            }).setOrigin(0.5)
        ];
        const cancelar = fonte.aoAvaliacaoVersus((avaliacao) => {
            if (!this.scene.isActive() || this.progresso.estado.avaliacao) return;
            this.progresso.registrarAvaliacao(avaliacao);
            this.mostrarPontuacao();                                   // RI-MV-15
        });
        this.events.once('shutdown', cancelar);
        fonte.solicitarAvaliacaoVersus({ turmaId: Sessao.atual.turmaId, grupoId: Sessao.atual.grupoId });
    }

    mostrarPontuacao () {
        this.tela.forEach((objeto) => objeto.destroy());
        this.tela = [];
        this.progresso.concluir();

        this.add.text(64, 201, t('versus.res_manchete'), ARCADE_TEXT.manchete).setOrigin(0, 0.5);
        this.add.rectangle(64, 238, 631, 1, ARCADE_HEX.TINTA).setOrigin(0, 0.5);
        let y = 262;
        PARAGRAFOS.map((chave) => t(chave)).forEach((paragrafo) => {
            y += criarTextoRico(this, 64, y, 632, paragrafo, { ...ARCADE_TEXT.introducao, fontSize: '26px' }, { pesoDestaque: '700', entrelinha: 29 }).altura + 27;
        });

        this.desenharPlacar(853, 238, montarPlacarVersus({
            rodadas: this.versus.rodadas, criterios: this.versus.criterios, avaliacao: this.progresso.estado.avaliacao
        }));

        // RI-MV-17: Continuar abre a confirmação de finalização.
        new BotaoArcade(this, 640, 721, { rotulo: t('geral.continuar'), aoClicar: () => this.abrirFinalizacao() });
    }

    /** Painel do quadro 79: total de cada rodada, a nota de cada critério e o TOTAL. */
    desenharPlacar (x, y, { blocos, total }) {
        const largura = 367, esquerda = x + 15, direita = x + largura - 15;
        const pixel = { ...ARCADE_TEXT.placar, fontSize: '22px' };
        const criterio = { fontFamily: ARCADE_TEXT.introducao.fontFamily, fontSize: '19px', fontStyle: '500', color: ARCADE.TINTA };
        const g = this.add.graphics();

        let cy = y + 32;
        blocos.forEach((bloco) => {
            this.add.text(esquerda, cy, bloco.rotulo, pixel).setOrigin(0, 0.5);
            this.add.text(direita, cy, String(bloco.total), pixel).setOrigin(1, 0.5);
            cy += 37;
            bloco.linhas.forEach((linha) => {
                const rotulo = this.add.text(x + 62, cy, linha.rotulo, criterio).setOrigin(0, 0.5);
                if (rotulo.width > 245) rotulo.setScale(245 / rotulo.width, 1);
                this.add.text(direita, cy, linha.pontos === null ? '-' : String(linha.pontos), { ...pixel, fontSize: '19px' }).setOrigin(1, 0.5);
                cy += 31;
            });
            cy += 12;
        });

        const fio = cy - 14;
        this.add.text(esquerda, fio + 30, t('geral.total'), { ...pixel, fontStyle: '700' }).setOrigin(0, 0.5);
        this.add.text(direita, fio + 30, String(total), pixel).setOrigin(1, 0.5);
        const altura = fio + 59 - y;
        g.fillStyle(ARCADE_HEX.BRANCO, 0.4).fillRoundedRect(x, y, largura, altura, 12);
        g.lineStyle(1, ARCADE_HEX.BRANCO, 1).strokeRoundedRect(x, y, largura, altura, 12);
        g.lineStyle(1.4, ARCADE_HEX.TINTA, 1).lineBetween(esquerda, fio, direita, fio);
    }

    abrirFinalizacao () {
        if (this.modal) return;
        this.modal = new ModalArcade(this, {
            titulo: t('versus.titulo'), largura: 820, centroY: 380, botaoY: 613,
            blocos: [
                { texto: t('versus.fim_titulo'), peso: '700', maiusculas: true },
                { texto: t('versus.fim_texto_1') },
                { texto: t('versus.fim_texto_2') },
                { texto: t('versus.fim_texto_3') }
            ],
            // RI-MV-18: quem devolve o grupo ao Aguarde é o facilitador (PRÓXIMA ESTAÇÃO, via `aoEstacao`); o botão
            // só existe no modo dev, que não tem facilitador.
            ...(this.registry.get('fonte')?.modo === 'dev' ? {
                botao: {
                    rotulo: t('geral.concluir'),
                    aoClicar: () => this.scene.start(SCENES.AGUARDE, { concluida: 'versus' })
                }
            } : {}),
            voltar: { rotulo: t('geral.voltar_pontuacao') },
            aoFechar: () => { this.modal = null; }
        });
    }
}
